import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import { TICKET_STUB_LAYOUT } from './TicketStub.constants';
import { TEAR_EDGE_PX } from './ticketStubJagged.helper';

export const TICKET_STUB_PALETTE = {
  stadiumDark: '#0e0719',
  stadiumDarkDeep: '#14081e',
  ticketWhite: '#ffffff',
  ticketInk: '#1a0d2e',
  ticketInkMuted: '#6b5e85',
  ticketInkFaint: '#9c8fb8',
  ticketTearLine: '#e7d9f5',
  ticketSubtleLine: '#f0e8fa',
} as const;

const {
  stadiumDark: STADIUM_DARK,
  ticketWhite: TICKET_WHITE,
  ticketInk: TICKET_INK,
  ticketInkMuted: TICKET_INK_MUTED,
  ticketInkFaint: TICKET_INK_FAINT,
  ticketTearLine: TICKET_TEAR_LINE,
  ticketSubtleLine: TICKET_SUBTLE_LINE,
} = TICKET_STUB_PALETTE;

export interface TicketStubStyleOptions {
  brandPrimary: string;
  brandSecondary: string | null;
  contentBottomInset?: number;
}

export const getTicketStubStyles = (theme: Theme, opts: TicketStubStyleOptions) => {
  const brandPrimary = opts.brandPrimary || theme.primary;
  return StyleSheet.create({
    root: {
      flex: 1,
      backgroundColor: STADIUM_DARK,
    },
    backdrop: {
      ...StyleSheet.absoluteFillObject,
    },
    halo: {
      position: 'absolute',
      top: 100,
      alignSelf: 'center',
      width: 340,
      height: 340,
      borderRadius: 170,
      backgroundColor: `${brandPrimary}44`,
      opacity: 0.6,
      // RN doesn't support blur filters in StyleSheet; rely on radial gradient
      // in the LinearGradient overlay for the soft halo effect.
    },
    scroll: {
      flexGrow: 1,
      paddingHorizontal: 18,
      paddingTop: Platform.OS === 'web' ? 32 : 56,
      paddingBottom: 24 + (opts.contentBottomInset ?? 0),
      alignItems: 'center',
    },
    frame: {
      width: '100%',
      maxWidth: TICKET_STUB_LAYOUT.FRAME_MAX_WIDTH,
    },
    headerRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 22,
    },
    headerLabel: {
      fontSize: 10,
      fontWeight: '800',
      letterSpacing: 2,
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.55)',
    },
    ticketWrap: {
      width: '100%',
      ...Platform.select({
        web: { filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.5))' as never },
        default: {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 24 },
          shadowOpacity: 0.45,
          shadowRadius: 30,
          elevation: 12,
        },
      }),
    },
    stub: {
      paddingHorizontal: 22,
      paddingTop: 22,
      paddingBottom: 24,
      backgroundColor: brandPrimary,
      borderTopLeftRadius: 22,
      borderTopRightRadius: 22,
      overflow: 'hidden',
    },
    stubGradient: {
      ...StyleSheet.absoluteFillObject,
    },
    stubDecoCircleLg: {
      position: 'absolute',
      top: -30,
      right: -30,
      width: 140,
      height: 140,
      borderRadius: 70,
      borderWidth: 2,
      borderColor: 'rgba(255,255,255,0.16)',
    },
    stubDecoCircleSm: {
      position: 'absolute',
      top: -10,
      right: -10,
      width: 100,
      height: 100,
      borderRadius: 50,
      borderWidth: 2,
      borderColor: 'rgba(255,255,255,0.1)',
    },
    stubTopRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    brandMark: {
      width: 42,
      height: 42,
      borderRadius: 10,
      backgroundColor: 'rgba(255,255,255,0.18)',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    brandMarkLogo: {
      width: 42,
      height: 42,
      borderRadius: 10,
    },
    brandMarkInitial: {
      fontSize: 18,
      fontWeight: '900',
      color: theme.white,
    },
    stubPresentedBy: {
      fontSize: 10,
      fontWeight: '800',
      letterSpacing: 1.6,
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.8)',
    },
    stubKicker: {
      fontSize: 13,
      fontWeight: '700',
      color: 'rgba(255,255,255,0.95)',
      marginTop: 1,
    },
    stubTitle: {
      fontSize: 28,
      lineHeight: 32,
      fontWeight: '900',
      letterSpacing: -1,
      color: theme.white,
      marginTop: 18,
    },
    stubTrophyRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginTop: 14,
    },
    stubTrophyText: {
      fontSize: 11,
      fontWeight: '600',
      color: 'rgba(255,255,255,0.9)',
      letterSpacing: 0.2,
    },
    perf: {
      height: 18,
      backgroundColor: TICKET_WHITE,
      position: 'relative',
    },
    perfNotchLeft: {
      position: 'absolute',
      left: -10,
      top: -10,
      width: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: STADIUM_DARK,
    },
    perfNotchRight: {
      position: 'absolute',
      right: -10,
      top: -10,
      width: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: STADIUM_DARK,
    },
    perfDashed: {
      position: 'absolute',
      left: 14,
      right: 14,
      top: 8,
      borderTopWidth: 2,
      borderStyle: 'dashed',
      borderColor: TICKET_TEAR_LINE,
    },
    perfHintWrap: {
      position: 'absolute',
      left: 0,
      right: 0,
      top: -2,
      alignItems: 'center',
    },
    perfHint: {
      backgroundColor: TICKET_WHITE,
      paddingHorizontal: 6,
      fontSize: 8,
      fontWeight: '800',
      letterSpacing: 2,
      color: TICKET_INK_FAINT,
    },
    body: {
      backgroundColor: TICKET_WHITE,
      borderBottomLeftRadius: 22,
      borderBottomRightRadius: 22,
      paddingHorizontal: 22,
      paddingTop: 14,
      paddingBottom: 22,
      position: 'relative',
      overflow: 'hidden',
    },
    bodyDefaultContent: {
      width: '100%',
    },
    bodyRevealOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 22,
      paddingVertical: 22,
      backgroundColor: TICKET_WHITE,
    },
    revealCheckCircle: {
      width: 64,
      height: 64,
      borderRadius: 32,
      backgroundColor: brandPrimary,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 14,
      ...Platform.select({
        web: {
          boxShadow: `0 10px 30px ${brandPrimary}66` as never,
        },
        default: {
          shadowColor: brandPrimary,
          shadowOffset: { width: 0, height: 10 },
          shadowOpacity: 0.45,
          shadowRadius: 18,
          elevation: 8,
        },
      }),
    },
    revealTitle: {
      fontSize: 20,
      fontWeight: '900',
      color: TICKET_INK,
      letterSpacing: -0.4,
      textAlign: 'center',
      lineHeight: 24,
    },
    revealSubtitle: {
      fontSize: 12,
      color: TICKET_INK_MUTED,
      fontWeight: '700',
      marginTop: 6,
      textAlign: 'center',
    },
    confettiPiece: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: 7,
      height: 11,
      borderRadius: 2,
      marginLeft: -3.5,
      marginTop: -5.5,
    },
    stubTearEdge: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: TEAR_EDGE_PX,
    },
    bodyTearEdge: {
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      height: TEAR_EDGE_PX,
    },
    bodySubtitle: {
      fontSize: 13,
      lineHeight: 18,
      color: TICKET_INK_MUTED,
      fontWeight: '600',
    },
    bodyMemberRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingTop: 12,
      paddingBottom: 12,
      borderTopWidth: 1,
      borderTopColor: TICKET_SUBTLE_LINE,
      marginTop: 14,
    },
    bodyMemberText: {
      flex: 1,
      fontSize: 11,
      color: TICKET_INK_MUTED,
      fontWeight: '700',
    },
    statsGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: 12,
    },
    statCell: {
      width: '50%',
      paddingVertical: 6,
    },
    statLabel: {
      fontSize: 9,
      fontWeight: '800',
      letterSpacing: 1.4,
      textTransform: 'uppercase',
      color: TICKET_INK_FAINT,
    },
    statValue: {
      fontSize: 13,
      fontWeight: '800',
      color: TICKET_INK,
      marginTop: 2,
    },
    primaryButton: {
      marginTop: 14,
      paddingVertical: 14,
      paddingHorizontal: 16,
      borderRadius: 12,
      backgroundColor: brandPrimary,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
      gap: 8,
      minHeight: 48,
    },
    primaryButtonText: {
      color: theme.white,
      fontSize: 15,
      fontWeight: '800',
      letterSpacing: -0.2,
    },
    primaryButtonHover: {
      opacity: 0.95,
    },
    primaryButtonPressed: {
      opacity: 0.85,
    },
    disabledNotice: {
      marginTop: 14,
      paddingVertical: 14,
      paddingHorizontal: 12,
      borderRadius: 12,
      backgroundColor: TICKET_SUBTLE_LINE,
      color: TICKET_INK_MUTED,
      fontSize: 13,
      fontWeight: '600',
      textAlign: 'center',
    },
    barcodeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginTop: 14,
    },
    barcodeStripes: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 1.5,
      height: 28,
    },
    barcodeBar: {
      backgroundColor: TICKET_INK,
      height: 28,
    },
    barcodeBarW1: { width: 2.5 },
    barcodeBarW2: { width: 5 },
    barcodeBarW3: { width: 7.5 },
    barcodeMeta: {
      flex: 1,
      alignItems: 'flex-end',
    },
    barcodeMetaLabel: {
      fontSize: 9,
      fontWeight: '800',
      letterSpacing: 1.2,
      textTransform: 'uppercase',
      color: TICKET_INK_FAINT,
    },
    barcodeMetaValue: {
      fontSize: 14,
      fontWeight: '700',
      color: TICKET_INK,
      letterSpacing: 1.5,
      ...Platform.select({ web: { fontFamily: 'JetBrains Mono, monospace' as never } }),
    },
    secondaryButton: {
      marginTop: 18,
      paddingVertical: 12,
      paddingHorizontal: 18,
      alignItems: 'center',
      alignSelf: 'center',
    },
    secondaryButtonText: {
      color: theme.white,
      fontSize: 13,
      fontWeight: '700',
      opacity: 0.85,
    },
    secondaryButtonHover: {
      opacity: 1,
    },
    continueWithAppButton: {
      marginTop: 14,
      alignSelf: 'stretch',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      paddingVertical: 14,
      paddingHorizontal: 18,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: 'rgba(255,255,255,0.25)',
      backgroundColor: 'rgba(255,255,255,0.06)',
      minHeight: 48,
    },
    continueWithAppButtonHover: {
      borderColor: 'rgba(255,255,255,0.45)',
      backgroundColor: 'rgba(255,255,255,0.12)',
    },
    continueWithAppText: {
      color: 'rgba(255,255,255,0.85)',
      fontSize: 14,
      fontWeight: '700',
      letterSpacing: -0.1,
    },
  });
};
